import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { MONITOR_KEY_QUERY_PARAM } from 'Config/proctoring.config'
import { decryptMonitorKey, validateMonitorKeyPayload } from 'Utils/monitorKey.utils'

/**
 * Optional `pc` query param from Talent Studio.
 * No `pc` → regular flow; invalid `pc` → fail closed.
 */
export const useMonitorKey = () => {
    const [searchParams] = useSearchParams()
    const encrypted = searchParams.get(MONITOR_KEY_QUERY_PARAM)
    const [state, setState] = useState<{
        isResolving: boolean
        isProctoredSession: boolean
        payload: {
            tenant: string
            profile: string
            duration: number
            reference_id: number
        } | null
        error: string | null
    }>({
        isResolving: !!encrypted,
        isProctoredSession: false,
        payload: null,
        error: null,
    })

    useEffect(() => {
        if (!encrypted) {
            setState({
                isResolving: false,
                isProctoredSession: false,
                payload: null,
                error: null,
            })
            return
        }

        let active = true
        setState(curr =>
            curr.isResolving
                ? curr
                : {
                      ...curr,
                      isResolving: true,
                      error: null,
                      payload: null,
                      isProctoredSession: false,
                  }
        )

        void decryptMonitorKey(encrypted)
            .then(payload => {
                if (!active) return
                const validationError = validateMonitorKeyPayload(payload)
                if (validationError) {
                    setState({
                        isResolving: false,
                        isProctoredSession: false,
                        payload: null,
                        error: validationError,
                    })
                    return
                }
                setState({
                    isResolving: false,
                    isProctoredSession: true,
                    payload: {
                        tenant: payload.tenant.trim(),
                        profile: payload.profile.trim(),
                        duration: payload.duration,
                        reference_id: payload.reference_id,
                    },
                    error: null,
                })
            })
            .catch(() => {
                if (!active) return
                setState({
                    isResolving: false,
                    isProctoredSession: false,
                    payload: null,
                    error: 'Unable to start proctored task. Invalid monitor key.',
                })
            })

        return () => {
            active = false
        }
    }, [encrypted])

    return state
}
